import { createServerFn } from "@tanstack/react-start";

export type SeasonalYear = { year: number; returnPct: number };
export type MonthStats = {
  month: number; // 0-11
  monthName: string;
  mtdPct: number | null; // only for the open (current) month
  avg1y: number | null;
  avg5y: number | null;
  avg10y: number | null;
  pos5y: number | null;
  pos10y: number | null;
  best: number | null;
  worst: number | null;
  years: SeasonalYear[]; // newest first, completed years only
  details: Record<number, { open: number; close: number }>;
};
export type SeasonalityDTO = {
  currentMonth: number;
  currentYear: number;
  months: MonthStats[];
  error?: string;
};

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const pos = (xs: number[]) => (xs.length ? (xs.filter((x) => x > 0).length / xs.length) * 100 : null);
const NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(Date.UTC(2000, i, 1)).toLocaleString("en-US", { month: "long", timeZone: "UTC" }),
);

// Most recent trading-day close (22:00 UTC) at or before `now`.
function lastDailyClose(now: Date): Date {
  const c = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 22));
  if (c > now) c.setUTCDate(c.getUTCDate() - 1);
  return c;
}

type Row = { year: number; month: number; open: number; close: number; is_complete: boolean; updated_at: string };

export const getGoldSeasonality = createServerFn({ method: "GET" }).handler(async (): Promise<SeasonalityDTO> => {
  const now = new Date();
  const month = now.getUTCMonth();
  const curYear = now.getUTCFullYear();

  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const read = async () => {
      const { data, error } = await supabaseAdmin
        .from("gold_monthly_history")
        .select("year,month,open,close,is_complete,updated_at")
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as Row[];
    };

    let rows = await read();
    const historyCount = rows.filter((r) => r.month === month + 1 && r.year < curYear && r.is_complete).length;
    const cur = rows.find((r) => r.year === curYear && r.month === month + 1);
    const curStale = !cur || new Date(cur.updated_at) < lastDailyClose(now);

    if (historyCount < 10 || curStale) {
      const { fetchGoldOHLC } = await import("@/server/twelve-data.server");
      const size = historyCount < 10 ? 180 : 2;
      const bars = await (fetchGoldOHLC as (i: string, n: number) => ReturnType<typeof fetchGoldOHLC>)("1month", size);
      const nowIso = now.toISOString();
      const upserts = bars
        .filter((b) => b.open > 0)
        .map((b) => {
          const d = new Date(b.time);
          const y = d.getUTCFullYear();
          const m = d.getUTCMonth();
          return {
            year: y,
            month: m + 1,
            open: b.open,
            close: b.close,
            is_complete: y < curYear || (y === curYear && m < month),
            updated_at: nowIso,
          };
        });
      if (upserts.length) {
        const { error } = await supabaseAdmin.from("gold_monthly_history").upsert(upserts, { onConflict: "year,month" });
        if (error) throw error;
      }
      rows = await read();
    }

    // Month to date uses the live spot cache when it is fresh, so intraday moves show immediately.
    let liveSpot: number | null = null;
    const { data: spotRow } = await supabaseAdmin.from("gold_price_cache").select("price,fetched_at").eq("id", 1).maybeSingle();
    if (spotRow && Number(spotRow.price) > 0 && now.getTime() - new Date(spotRow.fetched_at).getTime() < 30 * 60 * 1000) {
      liveSpot = Number(spotRow.price);
    }

    const months: MonthStats[] = NAMES.map((monthName, m) => {
      let mtdPct: number | null = null;
      const years: SeasonalYear[] = [];
      const details: Record<number, { open: number; close: number }> = {};
      for (const r of rows) {
        if (r.month !== m + 1) continue;
        const o = Number(r.open);
        if (!(o > 0)) continue;
        const isOpenMonth = r.year === curYear && m === month;
        const c = isOpenMonth && liveSpot != null ? liveSpot : Number(r.close);
        const pct = ((c - o) / o) * 100;
        if (isOpenMonth) mtdPct = pct;
        else if (r.year < curYear || (r.year === curYear && m < month)) {
          years.push({ year: r.year, returnPct: pct });
          details[r.year] = { open: o, close: c };
        }
      }
      years.sort((a, b) => b.year - a.year);
      const ten = years.slice(0, 10);
      const rs = ten.map((y) => y.returnPct);
      return {
        month: m,
        monthName,
        mtdPct,
        avg1y: rs[0] ?? null,
        avg5y: avg(rs.slice(0, 5)),
        avg10y: avg(rs),
        pos5y: pos(rs.slice(0, 5)),
        pos10y: pos(rs),
        best: rs.length ? Math.max(...rs) : null,
        worst: rs.length ? Math.min(...rs) : null,
        years: ten,
        details,
      };
    });
    return { currentMonth: month, currentYear: curYear, months };
  } catch (e) {
    console.error("[seasonality]", e);
    return { currentMonth: month, currentYear: curYear, months: [], error: "Seasonal data unavailable" };
  }
});
